// src/pages/Chat/MessageInput.jsx
import React, { useState, useEffect, useRef } from 'react';
import { IoSend, IoGlobe, IoMic, IoStop } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';


const MessageInput = ({ onSendMessage, isStreaming, initialContent = '' }) => {
    const { i18n } = useTranslation();
    const [content, setContent] = useState('');
    const [webSearchEnabled, setWebSearchEnabled] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const recognitionRef = useRef(null);

    const getRecognitionLanguage = (lang) => {
        const langMap = {
            'en': 'en-US',
            'fr': 'fr-FR',
            'de': 'de-DE'
        };
        return langMap[lang] || 'en-US';
    };
    
    useEffect(() => {
        // Only update if the current content is empty or the initialContent is different from current
        // This prevents overwriting user typing if they start typing before the effect runs
        if (content === '' || (initialContent !== '' && content !== initialContent)) {
             setContent(initialContent);
        }
    }, [initialContent]);
    
    // Initialize speech recognition
    useEffect(() => {
        // Check if browser supports speech recognition
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        
        if (SpeechRecognition) {
            setIsSupported(true);
            const recognition = new SpeechRecognition();
            
            // Configure recognition
            recognition.continuous = true; // Keep listening until stopped
            recognition.interimResults = true; // Show results as user speaks
            recognition.lang = getRecognitionLanguage(i18n.language);// Change to your preferred language
            
            // Handle results
            recognition.onresult = (event) => {
                let interimTranscript = '';
                let finalTranscript = '';
                
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                    } else {
                        interimTranscript += transcript;
                    }
                }
                
                // Update content with final transcript
                if (finalTranscript) {
                    setContent(prev => prev + finalTranscript);
                }
            };
            
            // Handle errors
            recognition.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
                
                if (event.error === 'not-allowed') {
                    alert('Microphone access denied. Please enable microphone permissions.');
                } else if (event.error === 'no-speech') {
                    // Silently handle no speech detected
                    console.log('No speech detected');
                }
            };
            
            // Handle end of recognition
            recognition.onend = () => {
                setIsListening(false);
            };
            
            recognitionRef.current = recognition;
        }
        
        // Cleanup
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const toggleListening = () => {
        if (!recognitionRef.current) return;
        
        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            try {
                recognitionRef.current.start();
                setIsListening(true);
            } catch (error) {
                console.error('Error starting recognition:', error);
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (content.trim() && !isStreaming) {
            // Stop listening if active
            if (isListening && recognitionRef.current) {
                recognitionRef.current.stop();
            }
            
            onSendMessage(content, webSearchEnabled);
            setContent('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const canSendMessage = !isStreaming && content.trim();

    return (
        <div className="p-4">
            <form onSubmit={handleSubmit} className="flex flex-col space-y-2">
                {/* Wrapper for the input area and buttons */}
                <div className="flex items-end space-x-3">
                    {/* Input Area Wrapper */}
                    <div className="flex-1 flex items-center bg-brand-white rounded-md overflow-hidden border border-slate-600">
                        
                        {/* Web Search Toggle Button */}
                        <button
                            type="button"
                            onClick={() => setWebSearchEnabled(!webSearchEnabled)}
                            className={`flex items-center px-2 py-1 m-1 rounded-sm transition-colors space-x-1 text-xs
                                ${webSearchEnabled ? 'bg-brand-orange text-black' : 'bg-slate-600 text-gray-300 hover:bg-slate-500'}`}
                            disabled={isStreaming}
                            aria-label="Toggle Web Search"
                        >
                            <IoGlobe size={16} />
                            <span>Search</span>
                        </button>

                        {/* Voice Input Button */}
                        {isSupported && (
                            <button
                                type="button"
                                onClick={toggleListening}
                                className={`flex items-center justify-center p-1.5 m-1 rounded-sm transition-all
                                    ${isListening 
                                        ? 'bg-brand-orange text-black animate-pulse' 
                                        : 'bg-brand-dark text-gray-300 hover:bg-slate-500'
                                    }`}
                                disabled={isStreaming}
                                aria-label={isListening ? "Stop listening" : "Start voice input"}
                                title={isListening ? "Stop listening" : "Start voice input"}
                            >
                                {isListening ? <IoStop size={12} /> : <IoMic size={12} />}
                            </button>
                        )}

                        {/* Textarea */}
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={isListening ? "Listening..." : "Type your message here..."}
                            className="flex-1 p-2 bg-transparent text-black border-0 focus:outline-none focus:ring-0 min-h-[40px] max-h-32 resize-none"
                            rows="1"
                            disabled={isStreaming}
                        />
                    </div>

                    {/* Send Button */}
                    <button
                        type="submit"
                        className={`flex items-center justify-center p-2.5 rounded-full transition-colors
                            ${canSendMessage ? 'bg-brand-orange hover:bg-orange-600 text-black' : 'bg-slate-700 text-slate-400 cursor-not-allowed'}
                        `}
                        disabled={!canSendMessage}
                    >
                        {isStreaming ? (
                            <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                        ) : (
                            <IoSend size={20} />
                        )}
                    </button>
                </div>

                {/* Optional: Listening indicator */}
                {isListening && (
                    <div className="text-xs text-red-400 flex items-center space-x-2 pl-2">
                        <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                        <span>Listening... Click the mic to stop</span>
                    </div>
                )}
            </form>
        </div>
    );
};

export default MessageInput;
